<template lang="pug">
    .result
        template(v-if="showProblem")
            .problem-wrapper
                .problem-header(:style="exampleStyle")
                    | {{ $i18n.t('trainer.trainer_gamePlayBox_example') }}
                .problem-body(:style="exampleStyle")
                    .problem-body__item.problem-body__item_number_example(
                        :class="{'problem-body__item_border_overlined': example.operation === 'root'}"
                    )
                    .problem-body__item
                        | {{ example + ' - ' + rightAnswer }}
                .problem-header.problem-header_your-answer
                    | {{ $i18n.t('trainer.trainer_gamePlayBox_yourAnswer') }}:
                .my-answer
                    template(v-if="answer !== null && answer !== ''")
                        | {{ playersAnswer }}
                    template(v-else)
                        | &mdash;
        template(v-else)
            .result__container(:class="resultContainerClasses" :style="resultContainerStyle")
                .result__reaction(:class="answerClasses")
                    .result__reaction-header
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_answer') }}
                    .result__reaction-number
                        template(v-if="answer !== null && answer !== ''")
                            | {{ playersAnswer }}
                        template(v-else)
                            | &mdash;
                result-image.result__reaction-image(:status="status", :is-multiplayer="!isSingleScreen")
                .result__reaction(:class="reactionClasses")
                    .result__reaction-header
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_right') }}
                    .result__reaction-number
                        | {{ rightAnswer }}
        .result__buttons-container
            template(v-if="gameEnd && isSingleScreen")
                result-buttons(
                    :exercise-id="exerciseId"
                    games-type="mentalArithmetic"
                    show-result-buttons
                    show-restart-button
                    @restartGame="$emit('restart', true)"
                )
                    template(slot="mainButton")
                        cs-button(
                            v-if='!showProblem && !isOlympiad',
                            type="button",
                            color="blue",
                            pattern="primary-outline",
                            @click="$emit('showProblem', true)"
                        )
                            | {{ $i18n.t('trainer.trainer_gamePlayBox_showProblem') }}
                    slot
            .result__buttons(v-else-if="!gameEnd")
                .result__button(v-if='!showProblem && !isOlympiad')
                    cs-button(
                        type="button",
                        color="blue",
                        pattern="primary-outline",
                        @click="$emit('showProblem', true)"
                    )
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_showProblem') }}
                .result__button
                    cs-button(
                        @click="$emit('restart')",
                        color="blue",
                        v-focus="true"
                    )
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
</template>

<script>
    import ResultImage from '../ResultImage.vue';
    import ResultButtons from '../ResultButtons.vue';

    export default {
        components: { ResultButtons, ResultImage },
        props: {
            answer: [Number, String],
            rightAnswer: [Number, String],
            gameEnd: Boolean,
            isSingleScreen: Boolean,
            place: Number,
            exerciseId: [Number, String],
            showProblem: {
                type: Boolean,
                default: false,
            },
            isOlympiad: {
                type: Boolean,
                default: false,
            },
            example: String,
            dictation: Boolean,
            colorForStudent: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                windowWidth: null,
            };
        },
        mounted() {
            this.playResultSound(this.answer === this.rightAnswer ? 'success' : 'failure');
            this.windowWidth = window.innerWidth;
        },
        beforeDestroy() {
            this.$emit('clearAnswer');
        },
        computed: {
            status() {
                return this.answer === this.rightAnswer ? 'success' : 'failure';
            },
            playersAnswer() {
                let maxNumberLength = 15;
                if (this.answer !== null) {
                    return +this.answer.toString().length <= maxNumberLength
                        ? this.answer
                        : +this.answer.toString().slice(0, maxNumberLength) + '...';
                }
                return undefined;
            },
            resultContainerClasses() {
                return {
                    'result__container--multiplayer': !this.isSingleScreen,
                };
            },
            answerClasses() {
                return {
                    result__reaction_correct: this.answer === this.rightAnswer,
                    result__reaction_incorrect: this.answer !== this.rightAnswer,
                    'result__reaction--multiplayer': !this.isSingleScreen,
                };
            },
            reactionClasses() {
                return {
                    'result__reaction--multiplayer': !this.isSingleScreen,
                };
            },
            exampleStyle() {
                return {
                    color: this.colorForStudent,
                };
            },
            resultContainerStyle() {
                if (
                    this.playersNumber > 4 &&
                    (this.answer.toString().length > 11 || this.rightAnswer.toString().length > 11)
                ) {
                    return { 'flex-direction': 'column' };
                }
                return undefined;
            },
        },
        methods: {
            playResultSound(result) {
                this.resultInstance = this.$createjs.Sound.play(result);
                this.resultInstance.volume = 0.6;
            },
        },
    };
</script>

<style scoped lang="scss">
    .result {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        flex-shrink: 1;
    }

    .result__container {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
        @include stack-space($space-base);
        flex-direction: row;
        @include media($md) {
            flex-wrap: nowrap;
        }
        @media screen and (max-width: 655px) {
            justify-content: space-between;
        }
    }

    .result__reaction {
        align-self: normal;
        font-size: 24px;
        @include media($lg) {
            font-size: 40px;
        }
        font-weight: 500;
        @include media($sm) {
            font-size: 30px;
            width: auto;
        }
        @include media($md) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: flex-start;
        }
        color: $hsl-color-green-58;
        @include stack-space($space-base);
        @include media($sm) {
            @include stack-space(0);
        }
        @media screen and (max-width: 768px) {
            width: 50%;
        }

        &:first-child {
            @media screen and (max-width: 767px) {
                order: 1;
            }
        }

        &:last-child {
            @media screen and (max-width: 767px) {
                order: 2;
            }
        }
    }

    .result__reaction_correct {
        color: $hsl-color-green-58;
    }

    .result__reaction_incorrect {
        color: $hsl-color-red-62;
    }

    .result__reaction-image {
        @include stack-space($space-base);
        order: 3;
        margin: 0 auto;
        @include media($md) {
            order: 0;
            margin: 0;
        }
    }
    .result__buttons-container {
        display: flex;
        justify-content: center;
        text-align: center;
        flex-wrap: wrap;
    }

    .result__buttons {
        display: flex;
        flex-direction: column;
        margin: -10px 0;
        @include media($lg) {
            flex-wrap: wrap;
            justify-content: center;
            flex-direction: row;
        }
    }

    .result__button {
        padding: 10px;
    }

    .result__show-example {
        @media screen and (max-width: 640px) {
            width: 100%;
        }
    }

    .show-example-button {
        margin-bottom: 30px;
        width: 230px;
        @media screen and (min-width: 641px) {
            margin: 0 25px 30px 0;
        }
    }

    .second-button {
        margin-right: 25px;
    }

    .problem-body__item_border_overlined {
        position: relative;
    }

    .problem-body__item_border_overlined:after {
        position: absolute;
        content: '';
        border-top: 2px solid;
        top: 1px;
        left: -1px;
        right: -2px;
    }

    .example__operation_type_exponentiation {
        font-size: 50%;
        top: 0.5em;
    }

    .example__operation_type_root {
        transform: scaleY(1.2);
    }

    .problem-wrapper {
        font-style: normal;
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: #6bbd6c;
        @include stack-space($space-base);
    }

    .problem-body {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
    }

    .problem-body__item {
        margin: 0 5px;
    }

    .problem-body__item_number_example {
        margin: 0;
    }

    .problem-header_your-answer {
        padding-top: 20px;
    }

    .result__container--multiplayer {
        flex-direction: column;
        justify-content: flex-start;
        flex: 0 1 auto;
        margin-bottom: 16px;
    }

    .multiplayer {
        .result__reaction-image {
            order: 2;
        }
    }

    .result__reaction--multiplayer:first-child {
        order: 1;
        align-self: center;
    }

    .result__reaction--multiplayer:last-child {
        order: 3;
        align-self: center;
    }

    .multiplayer--multiline .result__container--multiplayer {
        flex-direction: row;
    }

    .multiplayer--multiline .result {
        justify-content: space-between;
    }

    $players-amount-sizes: (
        '12': (
            1,
        ),
        '6': (
            2,
        ),
        '4': (
            3,
        ),
        '3': (
            4,
        ),
    );

    .multiplayer {
        .result {
            .cs-button {
                font-weight: 500;
            }
        }
    }

    @each $col-size, $size in $players-amount-sizes {
        @if $size == 2 {
            .multiplayer__item--is-#{$col-size} .result__reaction-image {
                max-width: 153px;
                @include media($xl) {
                    max-width: 225px;
                }
            }
        } @else {
            .multiplayer__item--is-#{$col-size} .result__reaction-image {
                max-width: 100px;
                @include media($xl) {
                    max-width: 225px;
                }
                @include media($xxl) {
                    max-width: 260px;
                }
            }
        }

        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .result__reaction-image {
            max-width: 60px;
            @if $size != 4 {
                min-height: 120px !important;
            }
            @include media($xl) {
                max-width: 100px;
                @if $size != 4 {
                    min-height: 130px !important;
                }
                @if $size = 3 {
                    min-height: 80px !important;
                    @include media($xxl) {
                        max-height: 120px !important;
                    }
                }
            }
            @include media($xxl) {
                max-width: 160px;
                @if $size != 4 {
                    min-height: 140px !important;
                }
            }
        }

        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .result__reaction,
        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .problem-wrapper {
            justify-content: flex-end;
            font-size: 18px;
            line-height: 21px;
            @include media($xxl) {
                font-size: 22px;
                line-height: 26px;
            }
        }

        .multiplayer--multiline .multiplayer__item--is-#{$col-size} {
            .result__button {
                padding: 5px;
            }
            .cs-button {
                padding: 10px 20px !important;
            }
            @include media($xl) {
                .result__button {
                    padding: 10px;
                }
                .cs-button {
                    padding: 15px 30px !important;
                }
            }
            @if $size = 4 {
                .result {
                    justify-content: flex-start;
                }
                .result__container--multiplayer {
                    margin-bottom: 15px;
                    @include media($xl) {
                        margin-bottom: 30px;
                    }
                }
            }
        }
    }
    .result__reaction-image_not-display {
        display: none;
    }
</style>
